import React from 'react'
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline'
import { toast } from 'sonner'

/**
 * Top-of-page action bar for the WPS Preview. WPS-side parallel of
 * `PqrPreviewToolbar` — three thin click targets:
 *
 *   - Discard staged changes   (visible only when isDirty)
 *   - Save                     (DB only, no PDF; enabled even with blanks)
 *   - Save & Generate Report   (DB + PDF; gated on WPS# + Rev)
 *
 * "Edit identifiers" / "Edit approvals" no longer live in the toolbar.
 * Users open those modals by clicking the matching area on the
 * canvas (the click-through path was wired in slice 04 of
 * wps-preview-pixel-parity via `wpsFieldLinkRegistry` modal-routing).
 *
 * The parent owns the diff-confirm flow — both Save and Save &
 * Generate clicks bubble straight up to the page so the
 * `WpsDiffConfirmDialog` can interpose (slice C.3). When Save &
 * Generate is blocked by missing required fields, the button stays
 * interactive (aria-disabled) so a click can surface the exact
 * reason as a toast, and an inline warning is rendered underneath
 * so the same information is permanently visible.
 *
 * Added in `wps-product-parity-fixes` slice C.1.
 */

interface Props {
  isDirty: boolean
  busy: boolean
  saveAndGenerateDisabledReason?: string
  onDiscard: () => void
  onSave: () => void
  onSaveAndGenerate: () => void
}

export default function WpsPreviewToolbar({
  isDirty,
  busy,
  saveAndGenerateDisabledReason,
  onDiscard,
  onSave,
  onSaveAndGenerate
}: Props) {
  const blockedByReason = Boolean(saveAndGenerateDisabledReason)

  const handleSaveAndGenerateAttempt = () => {
    if (busy) return
    if (saveAndGenerateDisabledReason) {
      toast.error(saveAndGenerateDisabledReason)
      return
    }
    onSaveAndGenerate()
  }

  return (
    <div className="flex flex-wrap items-start gap-2">
      {isDirty ? (
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-1 text-hero-red-70"
          onClick={onDiscard}
          disabled={busy}
          data-testid="wps-toolbar-discard"
        >
          <ArrowUturnLeftIcon className="h-4 w-4" />
          Discard staged changes
        </button>
      ) : null}
      <button
        type="button"
        className="btn-secondary inline-flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={onSave}
        disabled={busy}
        data-testid="wps-toolbar-save"
      >
        <CheckCircleIcon className="h-4 w-4" />
        Save
      </button>
      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          className={`btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold ${
            busy || blockedByReason ? 'cursor-not-allowed opacity-50' : ''
          }`}
          onClick={handleSaveAndGenerateAttempt}
          disabled={busy}
          aria-disabled={busy || blockedByReason}
          title={saveAndGenerateDisabledReason}
          data-testid="wps-toolbar-save-and-generate"
        >
          <DocumentArrowDownIcon className="h-4 w-4" />
          Save &amp; Generate Report
        </button>
        {blockedByReason ? (
          <p
            role="status"
            className="max-w-xs text-right text-xs text-hero-red-70"
            data-testid="wps-toolbar-save-generate-blocked"
          >
            {saveAndGenerateDisabledReason}
          </p>
        ) : null}
      </div>
    </div>
  )
}
